import React, { useState, useLayoutEffect } from "react";
import { Box, Typography } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { getChipName } from "./components/work-type-images";

// De chips staan bewust buiten WorktypeChips: als ze binnen de render
// gedefinieerd zijn is elk een nieuw componenttype per render, dus React
// gooit ze (met de <img> van het scanner-logo) bij elke render weg en maakt
// ze opnieuw. Safari laat zo'n nieuwe <img> even leeg -> logo knippert bij
// het wisselen van case.
const adminDisplayMap = {
  Priority: "Prio",
  Remake: "Remake",
  AdditionalInfo: "Info",
  ContactRequired: "Contact",
};

const AcceptedChip = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        padding: "0px 8px 0px 6px",
        borderRadius: "4px",
        height: "24px",
        width: "fit-content",
        minWidth: 0,
        flexShrink: 0,
        backgroundColor: "transparent",
        border: "1px solid rgba(255, 255, 255, 0.6)",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        backgroundColor: "#4A9B64",
        flexShrink: 0,
        boxShadow: "0 0 0 2px rgba(74, 155, 100, 0.25), 0 0 7px 2px rgba(74, 155, 100, 0.40)",
      }} />
      <Typography
        sx={{
          color: "#FFF",
          fontFamily: "DM Sans",
          fontSize: "11px",
          fontWeight: 600,
          lineHeight: "24px",
          letterSpacing: "0.88px",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
          userSelect: "none",
        }}
      >
        Accepted
      </Typography>
    </Box>
);

const ReceiverChip = ({ isMultiCompany, receiver, location }) => {
  if (!isMultiCompany || !receiver) return null;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        padding: "4px 6px",
        borderRadius: "4px",
        height: "16px",
        width: "fit-content",
        gap: "4px",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
      }}
    >
      <Box component="img" src={location} alt="location" sx={{ width: 12, height: 12 }} />
      <Typography
        sx={{
          color: "#FFF",
          fontFamily: "DM Sans",
          fontSize: "11px",
          fontWeight: 600,
          lineHeight: "16px",
          letterSpacing: "0.88px",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {receiver}
      </Typography>
    </Box>
  );
};

const ScannerChip = ({ portals, brand }) => {
  const portal = portals?.[brand];
  if (!portal) return null;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        padding: "0px 6px",
        gap: "4px",
        borderRadius: "4px",
        height: "24px",
        width: "fit-content",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        transition: "background-color 0.3s",
      }}
    >
      <Box component="img" src={portal.logo} alt={portal.display} sx={{ width: 16, height: 16 }} />
      <Typography
        sx={{
          color: "#FFF",
          fontFamily: "DM Sans",
          fontSize: "11px",
          fontWeight: 600,
          lineHeight: "24px",
          letterSpacing: "0.88px",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
          userSelect: "none",
        }}
      >
        {portal.display}
      </Typography>
    </Box>
  );
};

const WorktypeChip = ({ displayName, count }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      padding: "0px 6px",
      borderRadius: "4px",
      height: "24px",
      width: "fit-content",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    }}
  >
    <Typography
      sx={{
        color: "#FFF",
        fontFamily: "DM Sans",
        fontSize: "11px",
        fontWeight: 600,
        lineHeight: "24px",
        letterSpacing: "0.88px",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        userSelect: "none",
      }}
    >
      {displayName}
    </Typography>
    {count > 1 && (
      <Box
        sx={{
          ml: 0.5,
          width: 16,
          height: 16,
          borderRadius: "50%",
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: "DM Sans",
            fontSize: "8px",
            fontWeight: 600,
            color: "#FFF",
            lineHeight: 1,
            userSelect: "none",
          }}
        >
          {count}
        </Typography>
      </Box>
    )}
  </Box>
);

const AdminChip = ({ displayName }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      padding: "0px 6px",
      borderRadius: "4px",
      height: "22px",
      width: "fit-content",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      border: "1px solid rgba(255, 255, 255, 0.25)",
    }}
  >
    <Typography
      sx={{
        color: "#FFF",
        fontFamily: "DM Sans",
        fontSize: "11px",
        fontWeight: 600,
        lineHeight: "22px",
        letterSpacing: "0.88px",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        userSelect: "none",
      }}
    >
      {displayName}
    </Typography>
  </Box>
);

function WorktypeChips({ row, worktypeMap, brand, portals, receiver, location, isMultiCompany, layoutAnimations = false }) {

  const [prevRowId, setPrevRowId] = useState(row.id);
  const isNewRow = prevRowId !== row.id;
  useLayoutEffect(() => {
    if (prevRowId !== row.id) setPrevRowId(row.id);
  }, [row.id]);

  // Returns a plain string chip label for a restoration
  const getDisplayName = (restoration) => {
    return getChipName(restoration); // getChipName already returns a string
  };

  const hasNoScans = (() => {
    try {
      const files = JSON.parse(row.stlfiles || "[]");
      return Array.isArray(files) && files.length === 0;
    } catch {
      return !row.stlfiles;
    }
  })();

  // Aggregate worktype counts keyed by string label
  const counts = {};
  // WorktypeChips.jsx — in de counts aggregation
  (row.worktypes ?? [])
    .filter(r => r.RestorationTypeID)  // ← skip lege/null RestorationTypeID
    .forEach((restoration) => {
      const displayName = getDisplayName(restoration);
      counts[displayName] = (counts[displayName] || 0) + 1;
    });

  const chipTransition = { type: "spring", stiffness: 500, damping: 30 };

  const siblings = [
    <ReceiverChip key="receiver" isMultiCompany={isMultiCompany} receiver={receiver} location={location} />,
    <ScannerChip key="scanner" portals={portals} brand={brand} />,
    ...Object.entries(counts).map(([displayName, count]) => (
      <WorktypeChip key={displayName} displayName={displayName} count={count} />
    )),
    ...Object.entries(row.administrative ?? {}).map(([key, value]) => {
      if (value !== true) return null;
      const displayName = adminDisplayMap[key] ?? key;
      return <AdminChip key={key} displayName={displayName} />;
    }),
    hasNoScans ? <AdminChip key="no-scans" displayName="No Scans" /> : null,
  ].filter(Boolean);

  return (
    <div style={{ display: "flex", gap: "3px", flexWrap: "wrap", pointerEvents: "none" }}>
      {layoutAnimations ? (
        <div style={{
          maxWidth: row.status === "accepted" ? "300px" : "0px",
          width: "auto",
          minWidth: 0,
          overflow: "hidden",
          marginRight:"-3px", // Compenseert de 3px gap aan de rechterkant van de accepted chip
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          
          transition: isNewRow ? "none" : "max-width 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        }}>
          {row.status === "accepted" && (
            <motion.div
            
              initial={isNewRow ? false : { opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ ...chipTransition, delay: isNewRow ? 0 : 0.1 }}
              style={{ display: "flex"}}
              
            >
              <AcceptedChip />
            </motion.div>
          )}
        </div>
      ) : (
        <AnimatePresence mode="popLayout" initial={false}>
          {row.status === "accepted" && (
            <motion.div
              key="accepted"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={chipTransition}
              style={{ display: "flex" }}
            >
              <AcceptedChip />
            </motion.div>
          )}
        </AnimatePresence>
      )}
      {siblings.map((chip) => (
        <div key={chip.key} style={{ display: "flex"}}>
          {chip}
        </div>
      ))}
    </div>
  );
}

export default WorktypeChips;